/**
 * LaTeX Debug Panel Component
 * Similar to Overleaf's debug panel - shows compilation errors and warnings
 */

import React, { useState } from 'react'
import {
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { parseLatexWarning } from '@utils/parseLatexWarning'

interface LaTeXDebugPanelProps {
  errors: string[]
  warnings: string[]
  isOpen: boolean
  onToggle: () => void
  onClose?: () => void
  className?: string
  onWarningClick?: (lineNumber: number | null, pageNumber: number | null, message: string) => void
  onErrorClick?: (lineNumber: number | null, pageNumber: number | null, message: string) => void
}

/**
 * Deduplicate array of strings, preserving order (first occurrence kept)
 */
const deduplicateStrings = (arr: string[]): string[] => {
  const seen = new Set<string>()
  const unique: string[] = []
  for (const item of arr) {
    // Normalize whitespace for comparison (trim and collapse multiple spaces)
    const normalized = item.trim().replace(/\s+/g, ' ')
    if (!seen.has(normalized)) {
      seen.add(normalized)
      unique.push(item) // Keep original formatting
    }
  }
  return unique
}

const LaTeXDebugPanel: React.FC<LaTeXDebugPanelProps> = ({
  errors,
  warnings,
  isOpen,
  onToggle,
  onClose,
  className = '',
  onWarningClick,
  onErrorClick
}) => {
  // Deduplicate warnings and errors to show only unique ones
  const uniqueErrors = React.useMemo(() => deduplicateStrings(errors), [errors])
  const uniqueWarnings = React.useMemo(() => deduplicateStrings(warnings), [warnings])
  
  const hasErrors = uniqueErrors.length > 0
  const hasWarnings = uniqueWarnings.length > 0
  const hasIssues = hasErrors || hasWarnings

  if (!hasIssues) {
    return null
  }

  return (
    <div className={clsx('border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800', className)}>
      {/* Panel Header */}
      <div
        className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          {hasErrors ? (
            <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />
          ) : hasWarnings ? (
            <InformationCircleIcon className="w-5 h-5 text-yellow-500" />
          ) : null}
          <div className="flex items-center gap-2">
            <span className="font-medium text-slate-900 dark:text-white">
              {hasErrors ? 'Compilation Errors' : 'Compilation Warnings'}
            </span>
            {hasErrors && (
              <span className="px-2 py-0.5 text-xs font-semibold bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-full">
                {uniqueErrors.length}
                {errors.length > uniqueErrors.length && (
                  <span className="ml-1 text-xs opacity-75">
                    ({errors.length} total)
                  </span>
                )}
              </span>
            )}
            {hasWarnings && (
              <span className="px-2 py-0.5 text-xs font-semibold bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded-full">
                {uniqueWarnings.length}
                {warnings.length > uniqueWarnings.length && (
                  <span className="ml-1 text-xs opacity-75">
                    ({warnings.length} total)
                  </span>
                )}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {onClose && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                onClose()
              }}
              className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded transition-colors"
              title="Close panel"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          )}
          {isOpen ? (
            <ChevronDownIcon className="w-5 h-5 text-slate-500" />
          ) : (
            <ChevronUpIcon className="w-5 h-5 text-slate-500" />
          )}
        </div>
      </div>

      {/* Panel Content */}
      {isOpen && (
        <div className="border-t border-slate-200 dark:border-slate-700 max-h-96 overflow-y-auto">
          {/* Errors Section */}
          {hasErrors && (
            <div className="p-4 space-y-3">
              <h4 className="text-sm font-semibold text-red-700 dark:text-red-400 flex items-center gap-2">
                <ExclamationTriangleIcon className="w-4 h-4" />
                Errors ({uniqueErrors.length}
                {errors.length > uniqueErrors.length && ` of ${errors.length}`})
              </h4>
              <div className="space-y-2">
                {uniqueErrors.map((error, index) => {
                  const parsed = parseLatexWarning(error, 'error')
                  const isClickable = parsed.lineNumber !== null || parsed.pageNumber !== null
                  
                  return (
                    <div
                      key={index}
                      onClick={() => {
                        if (isClickable && onErrorClick) {
                          onErrorClick(parsed.lineNumber, parsed.pageNumber, error)
                        }
                      }}
                      className={clsx(
                        "p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg",
                        isClickable && "cursor-pointer hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                      )}
                      title={isClickable ? "Click to navigate to this error" : undefined}
                    >
                      <div className="text-sm text-red-900 dark:text-red-200 font-mono whitespace-pre-wrap break-words">
                        {error}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Warnings Section */}
          {hasWarnings && (
            <div className={clsx('p-4 space-y-3', hasErrors && 'border-t border-slate-200 dark:border-slate-700')}>
              <h4 className="text-sm font-semibold text-yellow-700 dark:text-yellow-400 flex items-center gap-2">
                <InformationCircleIcon className="w-4 h-4" />
                Warnings ({uniqueWarnings.length}
                {warnings.length > uniqueWarnings.length && ` of ${warnings.length}`})
              </h4>
              <div className="space-y-2">
                {uniqueWarnings.map((warning, index) => {
                  const parsed = parseLatexWarning(warning, 'warning')
                  const isClickable = parsed.lineNumber !== null || parsed.pageNumber !== null
                  
                  return (
                    <div
                      key={index}
                      onClick={() => {
                        if (isClickable && onWarningClick) {
                          onWarningClick(parsed.lineNumber, parsed.pageNumber, warning)
                        }
                      }}
                      className={clsx(
                        "p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg",
                        isClickable && "cursor-pointer hover:bg-yellow-100 dark:hover:bg-yellow-900/30 transition-colors"
                      )}
                      title={isClickable ? "Click to navigate to this warning" : undefined}
                    >
                      <div className="text-sm text-yellow-900 dark:text-yellow-200 font-mono whitespace-pre-wrap break-words">
                        {warning}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default LaTeXDebugPanel

